
import { defineComponent, ref, getCurrentInstance, onMounted, computed, onUnmounted, watch, PropType, onDeactivated, onActivated, Ref } from 'vue';
import Cookies from 'js-cookie';
import AIFeedList from '../ai-feed-list/index.vue';
import { sendLog } from '@/common/radar';
import { AIFeedPageEnum, AIGenderType } from '@/store/modules/ai-feed';
import AITopBar from '../ai-top-bar/index.vue';
import AIInputBox from '../ai-input-box/index.vue';
import AIAudioPlayer from '../ai-audio-player/index.vue';
import { AIFeed } from '~/api/ai-feed';
import { AIChat, AIFeedbackMessageInfo, AIMessage } from '@/store/modules/ai-conversation';
import AIInfoPopup from '../ai-info-popup/index.vue';
import AIAgeConfirmPopup from '@/pages/aichat/components/ai-age-confirm/index.vue';
import AIToDesktopPopup from '../ai-to-desktop-popup/index.vue';
import AILuminaPopup from '../ai-ads-popup/index.vue';
import AIChatLocalizeService from '@/pages/aichat/scripts/ai-chat';
import { UserInfoByLogin } from '@/api/user';
import { logForClickInstallAction } from '../../scripts/logger';
import { useInstallPWA } from '../../hooks/useInstallPWA';
import { getQuery } from '~/common/utils';
import MessageActionPopup from '../message-action-popup/index.vue';
import MessageFeedbackPopup from '../message-feedback-popup/index.vue';
import useAppEnvironment from '../../hooks/useAppEnvironment';
import AIOpenApp from '../ai-open-app/index.vue';
import AIInstallAppPopup from '../ai-install-app-popup/index.vue';
import { useCallFantasayApp } from '../../hooks/useCallFantasayApp';
import { checkIfKwaiProApp } from '@/common/app';
import { MessageAudioAutoPlayerControllerInstance, AudioStatus, AudioAutoPlayMessage } from '../../scripts/message-audio-auto-player';
import device from '~/common/device-info';
import { callFantasayAppFromKConfNew, OnelinkSceneKey } from '@/common/call-app';

enum PopupOpenStatus {
    Close = 0,
    Opend = 1,
    Opening = 2,
}

const PopupIconMap = {
    InstallKwaiIcon: 'https://aws-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/install-kwai-icon.png',
    PushIcon: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/push-icon.png',
    InstallSnackIcon: 'https://aws-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/install-snack-icon.png',
    SharePopupTopIcon: 'https://aws-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/share-popup-top-icon.png',
    KwaiIcon: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/kwai-icon.png',
    SnackIcon: 'https://cdn-static.ap4r.com/kos/s101/nlav11312/kos/s101/nlav11312/pwa/mobile/snack-icon.png',
};

const AIChatPwaFeedContainerId = 'pwa-ai-feed-container';

const AIFeedComponent = defineComponent({
    components: {
        AIFeedList,
        AITopBar,
        AIInputBox,
        AIAudioPlayer,
        AIInfoPopup,
        AIAgeConfirmPopup,
        AIToDesktopPopup,
        AILuminaPopup,
        MessageActionPopup,
        MessageFeedbackPopup,
        AIOpenApp,
        AIInstallAppPopup,
    },
    props: {
        feedPage: {
            type: String as PropType<AIFeedPageEnum> | undefined,
            default: AIFeedPageEnum.AIForyou,
        },
        defaultFeeds: {
            type: Array as PropType<AIFeed[]>,
            default: undefined,
        },
    },
    setup(props) {
        const { proxy } = getCurrentInstance() as { proxy: Record<string, any> };
        const isKwaiProAppEnv = checkIfKwaiProApp();

        const pushPopupVisible = ref(false);
        const canShowSwipeGuider = ref(false);
        const currentFeed = ref<AIFeed>();
        const audioPlayerRef = ref();
        const currentIndex = ref<number>(0);

        // const isShowMessageFeedback = ref(false);

        const messageFeedbackPopupVisible = computed(() => {
            return (proxy?.$store?.state?.conversationModule?.feedbackMessageInfoMap?.[currentFeed?.value?.bot_id_str || ''] as AIFeedbackMessageInfo)
                ?.showFeedbackPopup;
        });

        const handleMessageFeedbackLog = (feedbackContent?: string) => {
            const feedbackMessage = proxy?.$store?.state?.conversationModule?.feedbackMessageInfoMap?.[
                currentFeed?.value?.bot_id_str || ''
            ] as AIFeedbackMessageInfo;

            sendLog({
                type: 'click',
                name: 'MESSAGE_FEEDBACK_POPUP',
                value: {
                    click_area: 'dislike',
                    bot_id: feedbackMessage?.botId || '',
                    message_index: Number(feedbackMessage?.messageIndex || 0) + 1,
                    feedback_content: feedbackContent,
                },
            });
        };

        const handleMessageFeedbackPopupOk = (feedback?: string) => {
            handleMessageFeedbackLog(feedback);
            const feedbackMessageInfo =
                proxy?.$store?.state?.conversationModule?.feedbackMessageInfoMap?.[currentFeed?.value?.bot_id_str || ''] || {};
            proxy?.$store?.commit?.('conversationModule/setFeedbackMessageInfo', {
                [currentFeed?.value?.bot_id_str || '']: {
                    ...feedbackMessageInfo,
                    showFeedbackPopup: false,
                },
            });
        };

        const handleMessageFeedbackPopupCancel = () => {
            handleMessageFeedbackLog();
            const feedbackMessageInfo =
                proxy?.$store?.state?.conversationModule?.feedbackMessageInfoMap?.[currentFeed?.value?.bot_id_str || ''] || {};
            proxy?.$store?.commit?.('conversationModule/setFeedbackMessageInfo', {
                [currentFeed?.value?.bot_id_str || '']: {
                    ...feedbackMessageInfo,
                    showFeedbackPopup: false,
                },
            });
        };

        const messageActionListPopupVisible = computed(() => {
            return !!proxy?.$store?.state?.conversationModule?.selectedMessageInfo;
        });

        const handleHideMessageActionList = () => {
            proxy?.$store?.commit?.('conversationModule/setSelectedMessageInfo', undefined);
        };

        const {
            installPWAPopupVisible,

            handleInstallPWAPopupCancel,
            handleInstallPWAPopupConfirm,
            changeChatRoundCount,
            dynamicSetMainfest,
            resetPWAMainfest,
            checkIfAIPwaMode,
        } = useInstallPWA();

        const {
            installAppPopupVisible,

            handleCallFantasayAppConfirm,
            handleCallFantasayAppCancle,
            handleMessageSendCountChange,
        } = useCallFantasayApp();

        /* Computeds */
        // basic info
        const appName = computed(() => {
            const name = (getCurrentInstance()?.proxy as { $appName?: string })?.$appName;
            return name || '';
        });

        const userInfoByLogin = computed<UserInfoByLogin>(() => {
            const userInfoByLogin = (proxy?.$store?.state?.userModule?.userInfo || {}) as UserInfoByLogin;
            return userInfoByLogin;
        });

        const userId = computed(() => {
            const { user, userId, user_id } = userInfoByLogin.value || {};
            const uid = userId || user_id || user?.user_id || Cookies.get('userId');
            return uid; //  '150001356314096'
        });

        const installPopupVisible = computed<boolean>(() => {
            return proxy?.$store?.state?.popupModule?.installPWA;
        });

        const showAIInfoPopup = computed<boolean>(() => {
            return proxy?.$store?.state?.popupModule?.showAIInfo && !isKwaiProAppEnv;
        });

        const lastMessage = computed<AIMessage>(() => {
            const { conversations } = proxy?.$store?.state?.conversationModule;
            const chat: AIChat = conversations?.[currentFeed.value?.bot_id_str || ''];
            const messages = chat?.messages;
            return messages?.[messages?.length - 1];
        });

        const { addUser } = AIChatLocalizeService();

        // 18 confirm
        const shouldShowAIAgeConfirm = computed(() => {
            const showOver18 = proxy?.$store?.state?.popupModule?.showOver18 as boolean;
            return showOver18;
        });

        const playTotalCount = computed<number>(() => {
            return MessageAudioAutoPlayerControllerInstance.playTotalCount.value;
        });

        const handleAIAgeConfirmPopupConfirm = async () => {
            await addUser(`${userId.value}`, true);
            proxy?.$store?.commit?.('popupModule/setshowOver18', false);
        };
        const handleAIAgeConfirmPopupClose = async () => {
            proxy?.$store?.commit?.('popupModule/setshowOver18', false);
            await addUser(`${userId.value}`, false);
        };

        const handleClickMute = () => {
            proxy.$nextTick(() => {
                console.log('social-ai', 'changeMuteStatus', audioPlayerRef?.value);
            });
        };

        // feed change callback
        const handleFeedChange = (params?: { feed?: AIFeed; newIndex?: number }) => {
            const { feed, newIndex } = params || {};
            if (currentFeed.value) {
                currentFeed.value.index = 0;
            }
            currentFeed.value = feed;
            currentIndex.value = newIndex || 0;
            console.log('social-ai', 'handleFeedChange', currentFeed, feed, newIndex);
        };

        const handleSlideFeedTo = (params?: { feed?: AIFeed; toIndex?: number }) => {
            audioPlayerRef.value.pauseAudio();
        };

        const handleHideSwipeGuide = (hide: boolean) => {
            canShowSwipeGuider.value = !hide;
        };

        const handleGenderSelect = (gender: AIGenderType) => {
            proxy?.$store?.commit?.('aiFeedMoudle/setGenderPrefer', gender);
        };

        const handleCloseGenderPopup = () => {
            proxy?.$store?.commit?.('popupModule/setShowGenderPrefer', false);
        };

        // 广告
        const showAdsPopup = computed(() => {
            return proxy?.$store?.state?.popupModule?.showAdsPopup as boolean;
        });
        const canShowAds = computed(() => {
            const { pwa_source, skip_rtb_ab } = getQuery() || {};
            if (pwa_source !== 'rtb_afriend') return false;
            const social_ai_ads_string = proxy?.$store?.state?.startup?.social_ai_ads;
            if (!social_ai_ads_string && skip_rtb_ab !== 'true') return false;
            let social_ai_ads;
            try {
                social_ai_ads = JSON.parse(social_ai_ads_string);
            } catch (error) {
                // eslint-disable-next-line no-console
                console.warn('Parse app ads_time_control error: ', error);
            }
            console.log('lumina-ad', 'aichat-ads', proxy?.$store?.state?.startup, social_ai_ads);
            const { lumina_ads_start_pos, lumina_ads_load } = social_ai_ads || {};
            const start_pos = Number(lumina_ads_start_pos) > 0 ? Number(lumina_ads_start_pos) : 1;
            const ads_load = Number(lumina_ads_load) > 0 ? Number(lumina_ads_load) : 4;
            console.log('lumina-ad', 'aichat-ads', start_pos, ads_load);
            return currentIndex.value >= start_pos && ads_load > 0 && (currentIndex.value - start_pos) % ads_load === 0;
        });
        const canContinueChat = ref(true); // finish watch ads
        const showedAdsIndexes: Ref<number[]> = ref([]);
        const handleCloseAdsPopup = (canContinue: boolean) => {
            proxy?.$store?.commit?.('popupModule/setShowAdsPopup', false);
            canContinueChat.value = canContinue;
            if (!canContinue) {
                showedAdsIndexes.value.pop();
            }
        };
        const tryToshowAdsPopup = () => {
            if (!showedAdsIndexes.value?.includes(currentIndex.value)) {
                proxy?.$store?.commit?.('popupModule/setShowAdsPopup', true);
                showedAdsIndexes.value.push(currentIndex.value);
            }
        };
        const handleOnClickInputBox = () => {
            if (!canContinueChat.value && canShowAds.value) {
                tryToshowAdsPopup();
            }
        };

        // others
        const cancelMuted = () => {
            proxy?.$store?.commit?.('aiFeedMoudle/setMuted', false);
            document.removeEventListener('click', cancelMuted);
        };

        const audioPlayMessageId = computed(() => {
            return proxy?.$store?.state?.conversationModule?.audioPlayMessageId;
        });

        watch(
            () => audioPlayMessageId.value,
            newVal => {
                if (newVal && newVal.length > 0) {
                    audioPlayerRef?.value?.pauseAudio();
                }
            }
        );

        const { isAiChatApp } = useAppEnvironment();

        const installPWA = async () => {
            try {
                if (!window?.deferredPrompt) {
                    proxy?.$store?.commit?.('popupModule/setInstallPWA', false);
                    return;
                }
                await window.deferredPrompt?.prompt?.();
                const res = await window.deferredPrompt?.userChoice;
                sendLog({
                    type: 'click',
                    name: 'INSTALLED_PWA',
                    value: {
                        type: 'BACKGROUND_TASK_EVENT',
                        isAIChat: 'true',
                        is_install: res.outcome === 'accepted' ? PopupOpenStatus.Opening : PopupOpenStatus.Close,
                    },
                });
                // @ts-ignore
                window.deferredPrompt = null;
                proxy?.$store?.commit?.('popupModule/setInstallPWA', false);
            } catch (error) {
                console.log(error, 'error');
            }
            logForClickInstallAction('ok', currentFeed.value);
        };
        watch(
            () => lastMessage.value,
            newVal => {
                if (!newVal) {
                    console.log('social-ai', 'last message is null or undefined');
                }
                // 后续版本的逻辑 @xq
                // proxy.$nextTick(async () => {
                //     audioPlayerRef?.value?.pauseAudio();
                //     await audioPlayerRef?.value?.synthesisSpeeach();
                // });
            }
        );

        watch(
            () => installPopupVisible.value,
            newVal => {
                if (newVal) {
                    installPWA();
                }
            }
        );

        watch(
            () => canShowAds.value,
            newVal => {
                if (newVal) {
                    tryToshowAdsPopup();
                }
            }
        );

        watch(
            () => playTotalCount.value,
            newVal => {
                const { isIOS } = device();
                if (isIOS) {
                    const storeLink = 'https://apps.apple.com/app/weeeye-novo-ia-chat/id6743759736';
                    window.open(storeLink, '_blank');
                    return;
                }
                const { audio_play_count_to_app } = proxy?.$store?.state?.abTestModule?.abTestParams;
                if (newVal >= audio_play_count_to_app + 1 && isKwaiProAppEnv) {
                    setTimeout(() => {
                        MessageAudioAutoPlayerControllerInstance.onPause();
                        MessageAudioAutoPlayerControllerInstance.onDestroy();
                    });

                    callFantasayAppFromKConfNew({
                        scene: OnelinkSceneKey.WebAiChatMessageAudioPlay,
                        proxy,
                        customsParams: {
                            callAppPath: `botdetail/${currentFeed?.value?.bot_id_str || ''}`,
                        },
                    });
                }
            }
        );

        onMounted(async () => {
            // sendOpenPush();
            // app环境和pwa桌面版默认取消静音
            if (proxy?.$isAppEnvironment || checkIfAIPwaMode()) {
                proxy?.$store?.commit?.('aiFeedMoudle/setMuted', false);
            }

            document.addEventListener('click', cancelMuted);
            await audioPlayerRef?.value?.synthesisSpeeach();
            proxy.$nextTick(async () => {
                audioPlayerRef?.value?.playAudio();
            });

            if ((props?.feedPage === AIFeedPageEnum.AIChatRoom || props?.feedPage === AIFeedPageEnum.AIHistory) && props?.defaultFeeds) {
                currentFeed.value = props.defaultFeeds.at(0);
                currentIndex.value = 0;
            }

            (proxy as Record<string, any>).$bus.off('changeChatRoundCount', changeChatRoundCount);
            (proxy as Record<string, any>).$bus.on('changeChatRoundCount', changeChatRoundCount);
        });

        onUnmounted(() => {
            document.removeEventListener('click', cancelMuted);
        });

        watch(
            () => currentFeed.value,
            async newVal => {
                if (newVal) {
                    dynamicSetMainfest({
                        name: currentFeed?.value?.bot_name,
                        icon: currentFeed?.value?.head_url,
                        botID: currentFeed?.value?.bot_id_str,
                    });
                }
            }
        );

        watch(
            () => props.defaultFeeds,
            newVal => {
                if (newVal) {
                    if ((props?.feedPage === AIFeedPageEnum.AIChatRoom || props?.feedPage === AIFeedPageEnum.AIHistory) && props?.defaultFeeds) {
                        currentFeed.value = newVal.at(0);
                        currentIndex.value = 0;
                    }
                }
            }
        );

        watch(
            () => currentIndex.value,
            newVal => {
                if (proxy?.$isAppEnvironment || newVal < 1) {
                    return;
                }
                const { isIOS } = device();
                const storeLink = isIOS ? 'https://apps.apple.com/app/weeeye-novo-ia-chat/id6743759736' : 'https://fantasay.onelink.me/888I/51jo203u';
                window.open(storeLink, '_blank');
            }
        );

        onActivated(() => {
            setTimeout(async () => {
                dynamicSetMainfest({
                    name: currentFeed?.value?.bot_name,
                    icon: currentFeed?.value?.head_url,
                    botID: currentFeed?.value?.bot_id_str,
                });
            }, 500);
        });

        onDeactivated(() => {
            resetPWAMainfest();
        });

        return {
            AIChatPwaFeedContainerId,
            appName,
            installPopupVisible,
            pushPopupVisible,
            audioPlayerRef,
            currentFeed,
            lastMessage,
            currentIndex,
            PopupIconMap,
            AIFeedPageEnum: props.feedPage || AIFeedPageEnum.AIForyou,
            canShowSwipeGuider,
            showAIInfoPopup,
            shouldShowAIAgeConfirm,
            installPWAPopupVisible,
            showAdsPopup,
            messageActionListPopupVisible,
            messageFeedbackPopupVisible,
            installAppPopupVisible,
            isKwaiProAppEnv,

            handleGenderSelect,
            handleFeedChange,
            handleSlideFeedTo,
            handleOnClickInputBox,
            handleHideSwipeGuide,
            handleClickMute,
            handleAIAgeConfirmPopupConfirm,
            handleAIAgeConfirmPopupClose,
            handleInstallPWAPopupCancel,
            handleInstallPWAPopupConfirm,
            handleCloseGenderPopup,
            handleCloseAdsPopup,
            handleHideMessageActionList,
            handleMessageFeedbackPopupCancel,
            handleMessageFeedbackPopupOk,
            handleCallFantasayAppConfirm,
            handleCallFantasayAppCancle,
            handleMessageSendCountChange,
        };
    },
});

export default AIFeedComponent;
